import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { TemplateVariant } from './notificationTemplates';
import './TemplatePickerModal.css';

interface Props {
    /** Heading shown at the top of the modal. */
    title: string;
    /** Ready-made copy variants the owner can pick from. */
    variants: TemplateVariant[];
    /** Current textarea content — used to warn that applying replaces it. */
    currentText: string;
    onApply: (text: string) => void;
    onClose: () => void;
}

/**
 * Modal template picker. Replaces the old window.confirm: shows the available
 * pre-built templates with a preview of each, and applies the chosen one to the
 * textarea. Rendered through a portal on document.body so it overlays the whole
 * screen regardless of where the card sits.
 */
const TemplatePickerModal: React.FC<Props> = ({ title, variants, currentText, onApply, onClose }) => {
    const { t } = useTranslation();

    // Close on Escape.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const hasText = currentText.trim().length > 0;

    return ReactDOM.createPortal(
        <div className="tpl-modal__overlay" onMouseDown={onClose} role="presentation">
            <div
                className="tpl-modal"
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onMouseDown={(e) => e.stopPropagation()}
                data-cy="template-picker-modal"
            >
                <div className="tpl-modal__head">
                    <div className="tpl-modal__title">
                        <FontAwesomeIcon icon={faWandMagicSparkles} className="tpl-modal__title-icon" />
                        {title}
                    </div>
                    <button
                        type="button"
                        className="tpl-modal__close"
                        onClick={onClose}
                        aria-label={t('settings.templatePicker.closeAriaLabel')}
                        data-cy="template-picker-close"
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                <p className="tpl-modal__sub">
                    {hasText
                        ? t('settings.templatePicker.subWithReplace')
                        : t('settings.templatePicker.sub')}
                </p>

                <div className="tpl-modal__list">
                    {variants.map((v) => (
                        <div className="tpl-card" key={v.name}>
                            <div className="tpl-card__head">
                                <span className="tpl-card__name">{v.name}</span>
                                <button
                                    type="button"
                                    className="tpl-card__apply"
                                    onClick={() => onApply(v.text)}
                                    data-cy={`template-apply-${v.name}`}
                                >
                                    {t('settings.templatePicker.useThis')}
                                </button>
                            </div>
                            <p className="tpl-card__preview">{v.text}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TemplatePickerModal;
